import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Trash2, Plus } from "lucide-react";

const STATUS_TONE = { AUTO: "text-emerald-700", SUGGESTED: "text-amber-700", UNMAPPED: "text-red-700" };

function ItemSelect({ value, items, onChange, testid, disabled }) {
  return (
    <select className="h-8 w-full rounded border border-stone-300 bg-white px-1 text-xs" value={value || ""} disabled={disabled}
      onChange={(e) => onChange(items.find((i) => i.item_code === e.target.value))} data-testid={testid}>
      <option value="">— not mapped —</option>
      {items.map((i) => <option key={i.item_code} value={i.item_code}>{i.item_name} ({i.unit})</option>)}
    </select>
  );
}

function RateInput({ l, i, prefix, onSaveRate }) {
  const [v, setV] = useState("");
  const [scope, setScope] = useState("CENTRE");
  return (
    <div className="flex gap-1 mt-1" data-testid={`${prefix}-rate-edit-${i}`}>
      <Input className="h-7 w-20 text-xs" type="number" step="0.01" placeholder={`per ${l.item_unit || "unit"}`} value={v} onChange={(e) => setV(e.target.value)} data-testid={`${prefix}-rate-input-${i}`} />
      <select className="h-7 rounded border text-[10px]" value={scope} onChange={(e) => setScope(e.target.value)} data-testid={`${prefix}-rate-scope-${i}`}>
        <option value="CENTRE">This centre</option><option value="DEFAULT">Default</option>
      </select>
      <Button size="sm" className="h-7 px-2 text-[10px]" disabled={!v} onClick={() => onSaveRate(l, parseFloat(v), scope)} data-testid={`${prefix}-rate-save-${i}`}>Save rate</Button>
    </div>
  );
}

export const CostingLines = ({ lines, setLines, items, editable, cur, prefix, packing, rateEdit, onSaveRate }) => {
  const upd = (i, patch) => setLines(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  return (
    <div className="space-y-1" data-testid={`${prefix}-lines`}>
      <div className="grid grid-cols-[1.4fr_1.6fr_70px_60px_70px_1.3fr_28px] gap-1 text-[10px] font-semibold uppercase text-stone-500">
        <span>{packing ? "Packing item" : "Recipe text"}</span><span>Raw material</span><span>Qty</span><span>Unit</span>
        <span>Fallback</span><span>Rate · Cost</span><span />
      </div>
      {lines.map((l, i) => (
        <div key={i} className="grid grid-cols-[1.4fr_1.6fr_70px_60px_70px_1.3fr_28px] items-center gap-1 text-xs" data-testid={`${prefix}-line-${i}`}>
          <span className="truncate" title={l.text}>
            {packing ? l.item_name || "—" : l.text}
            {l.match_status && !l.confirmed && <span className={`ml-1 text-[10px] ${STATUS_TONE[l.match_status] || ""}`}>{l.match_status}</span>}
          </span>
          <ItemSelect value={l.item_code} items={items} testid={`${prefix}-item-${i}`} disabled={!editable}
            onChange={(it) => upd(i, { item_code: it?.item_code || null, item_name: it?.item_name || null, item_unit: it?.unit || null, match_status: it ? "CONFIRMED" : "UNMAPPED" })} />
          <Input className="h-8 text-xs" type="number" step="0.001" disabled={!editable} value={l.qty ?? ""} placeholder={l.status === "AS_REQUIRED" ? "as req." : ""}
            onChange={(e) => upd(i, { qty: e.target.value === "" ? null : parseFloat(e.target.value), status: "PARSED" })} data-testid={`${prefix}-qty-${i}`} />
          <Input className="h-8 text-xs" disabled={!editable} value={l.unit || ""} onChange={(e) => upd(i, { unit: e.target.value })} data-testid={`${prefix}-unit-${i}`} />
          <Input className="h-8 text-xs" type="number" step="0.01" disabled={!editable} value={l.fallback_rate ?? ""} placeholder="—"
            onChange={(e) => upd(i, { fallback_rate: e.target.value === "" ? null : parseFloat(e.target.value) })} data-testid={`${prefix}-fallback-${i}`} />
          <span className={`text-[11px] ${l.issue ? "text-red-700" : "text-stone-700"}`} data-testid={`${prefix}-cost-${i}`}>
            {l.rate_missing ? <b data-testid={`${prefix}-rate-missing-${i}`}>Rate Missing — {cur}0.00</b>
              : l.issue ? l.issue
              : <>{cur}{Number(l.rate).toFixed(2)}/{l.item_unit || ""} · <b>{cur}{Number(l.cost).toFixed(2)}</b><span className="block text-[10px] text-stone-500">{l.rate_label}</span></>}
            {onSaveRate && l.item_code && (l.rate_missing || rateEdit) && <RateInput l={l} i={i} prefix={prefix} onSaveRate={onSaveRate} />}
          </span>
          {editable ? <button onClick={() => setLines(lines.filter((_, j) => j !== i))} data-testid={`${prefix}-remove-${i}`}><Trash2 className="h-3.5 w-3.5 text-red-500" /></button> : <span />}
        </div>
      ))}
      {editable && (
        <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`${prefix}-add`}
          onClick={() => setLines([...lines, { text: "", name: "", item_code: null, qty: null, unit: packing ? "pcs" : "", status: "PARSED" }])}>
          <Plus className="mr-1 h-3 w-3" /> Add {packing ? "packing item" : "ingredient line"}
        </Button>
      )}
    </div>
  );
};
